/**
 * Mina bokningar som dashboard-widget.
 * För en vanlig användare är den ofta det enda kortet, så den är helbredd och hög.
 */

import React, { useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { bookingApi } from '@/services/booking/bookingApi';
import { useLanguage, useTranslation } from '@/store/languageStore';
import {
  leadTimeUntil,
  electricSharePercent,
  monthlyBookingBars,
  periodKilometers,
  sixMonthBookingMix,
  upcomingBookings,
} from '@/utils/myBookingsWidget';
import MyBookingsList, { clock, DateBadge, useMyBookingsData, type MyBookingItem } from './MyBookingsList';
import MyBookingsMonthChart from './MyBookingsMonthChart';

function StatBox({ value, label }: { value: string; label: string }) {
  return (
    <div className="min-w-0 rounded-lg bg-bgBase px-2 py-1.5">
      <div className="truncate text-xl font-semibold text-textDefault">{value}</div>
      <div className="truncate text-xs text-textSecondary">{label}</div>
    </div>
  );
}

function localYmd(iso: string): string {
  const date = new Date(iso);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

const MyBookingsWidget: React.FC = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const locale = language === 'en' ? 'en-GB' : 'sv-SE';
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { data, isLoading, error } = useMyBookingsData();

  const now = useMemo(() => new Date(), []);
  const bookings = useMemo(() => upcomingBookings(data?.items ?? [], now), [data, now]);
  const next = bookings[0] ?? null;
  const monthMix = useMemo(
    () => sixMonthBookingMix([...(data?.items ?? []), ...(data?.history ?? [])], now, timeZone),
    [data, now, timeZone]
  );
  const monthBars = useMemo(
    () => monthlyBookingBars(
      [...(data?.items ?? []), ...(data?.history ?? [])],
      now,
      locale,
      timeZone
    ),
    [data, now, locale, timeZone]
  );

  const openBooking = (item: MyBookingItem, mode: 'view' | 'edit' = 'view') => {
    const params = new URLSearchParams({
      pickupLocationId: String(item.pickupLocationId),
      date: localYmd(item.startAt),
      bookingId: String(item.id),
    });
    if (mode === 'edit') params.set('edit', '1');
    navigate(`/booking/calendar?${params.toString()}`);
  };

  const cancelBooking = async (item: MyBookingItem) => {
    if (item.status !== 'BOOKED' || item.awaitingVehicleAssignment) return;
    if (!window.confirm(t('booking.cancelBookingConfirm'))) return;
    setCancellingId(item.id);
    try {
      await bookingApi.selfCancelBooking(item.id);
      toast.success(t('booking.cancelBookingSuccess'));
      await queryClient.invalidateQueries(['dashboard-widget', '/api/dashboard/data/my-bookings']);
    } catch {
      toast.error(t('booking.cancelBookingError'));
    } finally {
      setCancellingId(null);
    }
  };

  const vehicleName = (item: MyBookingItem) =>
    item.awaitingVehicleAssignment ? t('bookingWidget.noVehicleAssigned') : item.vehicleLabel;

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error && !data) {
    return <div className="text-red-500 text-sm py-2">{(error as Error).message}</div>;
  }

  const lead = next ? leadTimeUntil(next.startAt, new Date()) : null;
  const leadLabel = !lead
    ? ''
    : lead.started
      ? t('bookingWidget.inProgress')
      : t('bookingWidget.startsIn').replace('{{hours}}', String(lead.hours)).replace('{{minutes}}', String(lead.minutes));

  const periodKm = periodKilometers(data?.period?.drivenKm ?? 0, data?.period?.estimatedKm ?? 0);
  const periodCost = data?.period?.costKr ?? 0;
  const numberFormat = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });

  return (
    <div className="dq-my-bookings flex h-full min-h-0 flex-col gap-3 overflow-hidden">
      <div className="flex shrink-0 items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-textDefault">{t('bookingWidget.title')}</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/booking/calendar')}
            className="text-xs text-textSecondary hover:text-textDefault"
          >
            {t('bookingWidget.openCalendar')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/booking/calendar?newBooking=1')}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-bgPrimary text-textLight text-sm font-medium"
          >
            <PlusIcon className="w-4 h-4" />
            {t('bookingWidget.newBooking')}
          </button>
        </div>
      </div>

      {next && lead ? (
        <div className="flex shrink-0 items-center gap-6 rounded-xl border border-borderDefault bg-bgBase/60 px-6 py-6">
          <DateBadge iso={next.startAt} locale={locale} todayLabel={t('bookingWidget.today')} timeZone={timeZone} />
          <div className="min-w-0 flex-1">
            <div className="text-sm uppercase tracking-wide text-textTertiary">
              {t('bookingWidget.nextBooking')} · {leadLabel}
            </div>
            <div className="mt-1 truncate text-3xl font-semibold text-textDefault">
              {clock(next.startAt, locale)}–{clock(next.endAt, locale)} · {vehicleName(next)}
            </div>
            <div className="mt-1 truncate text-xl text-textSecondary">
              {[next.customerName, next.pickupLocationName].filter(Boolean).join(' · ')}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => openBooking(next, 'edit')}
              className="rounded-xl border border-borderDefault px-4 py-2 text-base font-medium text-textDefault hover:bg-bgHover"
            >
              {t('bookingWidget.edit')}
            </button>
            {next.status === 'BOOKED' && (
              <button
                type="button"
                onClick={() => void cancelBooking(next)}
                disabled={cancellingId === next.id}
                className="rounded-xl border border-borderDefault px-4 py-2 text-base font-medium text-red-400 hover:bg-bgHover disabled:opacity-50"
              >
                {cancellingId === next.id ? t('common.loading') : t('booking.cancelBookingButton')}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="text-base text-textSecondary py-6 text-center">{t('bookingWidget.noBookings')}</div>
      )}

      <div className="dq-my-bookings-split">
        <MyBookingsList />
        <div className="flex min-h-0 min-w-0 flex-col">
          <MyBookingsMonthChart
            title={t('bookingWidget.perMonth')}
            rangeLabel={t('bookingWidget.sixMonths')}
            completedLabel={t('bookingWidget.completedSeries')}
            upcomingLabel={t('bookingWidget.upcomingSeries')}
            bars={monthBars}
          />
          <div className="mt-3 shrink-0 rounded-xl border border-borderDefault bg-bgBase/40 px-3 py-2">
            <h3 className="text-sm font-semibold text-textDefault">{t('bookingWidget.lastSixMonths')}</h3>
            <div className="mt-2 grid grid-cols-3 gap-2">
              <StatBox value={String(monthMix.total)} label={t('bookingWidget.bookingsCount')} />
              <StatBox value={numberFormat.format(periodKm)} label={t('bookingWidget.periodKm')} />
              <StatBox value={numberFormat.format(periodCost)} label={t('bookingWidget.periodCost')} />
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2">
              <StatBox value={String(monthMix.cars)} label={t('bookingWidget.cars')} />
              <StatBox value={String(monthMix.resources)} label={t('bookingWidget.resources')} />
              <StatBox
                value={`${electricSharePercent(monthMix.electricCars, monthMix.cars)}%`}
                label={t('bookingWidget.electricShare')}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyBookingsWidget;
